// Procedural kid-friendly sounds via Web Audio API.
// No external fetch, no CORS, no broken links. Generates fun sounds on demand.
//
// Sound design rationale (kid UX):
//  - "fanfare": triumphant major-chord arpeggio for routine completion
//  - "sparkle": high tinkly bell cascade for transitions (shower → brush)
//  - "powerup": rising zap inspired by anime power-ups
//  - "ding": simple two-tone bell for short cues
//  - "cheer": major triad stinger with vibrato for the final alarm
//  - "whoosh": filtered noise sweep for phase changes

let sharedCtx: AudioContext | null = null;
const getCtx = (): AudioContext => {
    if (!sharedCtx) {
        const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        sharedCtx = new Ctor();
    }
    if (sharedCtx.state === 'suspended') void sharedCtx.resume();
    return sharedCtx;
};

type ToneOpts = {
    freq: number;
    start: number;
    dur: number;
    type?: OscillatorType;
    gain?: number;
    vibrato?: number;
};

const playTones = (tones: ToneOpts[], masterGain = 0.18) => {
    const ctx = getCtx();
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = masterGain;
    master.connect(ctx.destination);

    for (const t of tones) {
        const osc = ctx.createOscillator();
        osc.type = t.type || 'sine';
        osc.frequency.value = t.freq;
        const g = ctx.createGain();
        const peak = t.gain ?? 0.8;
        // ADSR-ish envelope
        g.gain.setValueAtTime(0, now + t.start);
        g.gain.linearRampToValueAtTime(peak, now + t.start + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, now + t.start + t.dur);
        osc.connect(g);
        g.connect(master);

        if (t.vibrato) {
            const lfo = ctx.createOscillator();
            const lfoGain = ctx.createGain();
            lfo.frequency.value = t.vibrato;
            lfoGain.gain.value = t.freq * 0.02;
            lfo.connect(lfoGain);
            lfoGain.connect(osc.frequency);
            lfo.start(now + t.start);
            lfo.stop(now + t.start + t.dur);
        }

        osc.start(now + t.start);
        osc.stop(now + t.start + t.dur + 0.05);
    }
};

const playNoise = (start: number, dur: number, filterFreq: number, masterGain = 0.12) => {
    const ctx = getCtx();
    const now = ctx.currentTime;
    const buffer = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = filterFreq;
    filter.Q.value = 1.5;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, now + start);
    g.gain.linearRampToValueAtTime(masterGain, now + start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(ctx.destination);
    src.start(now + start);
    src.stop(now + start + dur + 0.05);
};

export type KidSound = {
    id: string;
    label: string;
    emoji: string;
    play: () => void;
};

export const KID_SOUNDS: KidSound[] = [
    {
        id: 'fanfare',
        label: 'Fanfare',
        emoji: '🎺',
        play: () => {
            // C major arpeggio fanfare
            playTones([
                { freq: 523.25, start: 0, dur: 0.15, type: 'square' }, // C5
                { freq: 659.25, start: 0.12, dur: 0.15, type: 'square' }, // E5
                { freq: 783.99, start: 0.24, dur: 0.15, type: 'square' }, // G5
                { freq: 1046.5, start: 0.36, dur: 0.5, type: 'square' }, // C6
            ], 0.15);
        },
    },
    {
        id: 'sparkle',
        label: 'Sparkle',
        emoji: '✨',
        play: () => {
            // High tinkly cascade
            playTones([
                { freq: 1318.5, start: 0, dur: 0.2, type: 'sine' },
                { freq: 1567.98, start: 0.08, dur: 0.2, type: 'sine' },
                { freq: 2093, start: 0.16, dur: 0.25, type: 'sine' },
                { freq: 2637, start: 0.24, dur: 0.3, type: 'sine' },
            ], 0.12);
        },
    },
    {
        id: 'powerup',
        label: 'Power Up',
        emoji: '⚡',
        play: () => {
            // Rising zap (anime-style power-up)
            const ctx = getCtx();
            const now = ctx.currentTime;
            const osc = ctx.createOscillator();
            osc.type = 'sawtooth';
            const g = ctx.createGain();
            g.gain.setValueAtTime(0, now);
            g.gain.linearRampToValueAtTime(0.2, now + 0.05);
            g.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
            osc.frequency.setValueAtTime(220, now);
            osc.frequency.exponentialRampToValueAtTime(880, now + 0.5);
            osc.connect(g);
            g.connect(ctx.destination);
            osc.start(now);
            osc.stop(now + 0.65);
            playNoise(0, 0.5, 800, 0.06);
        },
    },
    {
        id: 'ding',
        label: 'Ding Dong',
        emoji: '🔔',
        play: () => {
            playTones([
                { freq: 880, start: 0, dur: 0.3, type: 'sine' },
                { freq: 1318.5, start: 0.15, dur: 0.4, type: 'sine' },
            ], 0.2);
        },
    },
    {
        id: 'cheer',
        label: 'Cheer',
        emoji: '🎉',
        play: () => {
            // Major triad stinger with vibrato
            playTones([
                { freq: 523.25, start: 0, dur: 0.8, type: 'triangle', vibrato: 6 },
                { freq: 659.25, start: 0.05, dur: 0.8, type: 'triangle', vibrato: 6 },
                { freq: 783.99, start: 0.1, dur: 0.9, type: 'triangle', vibrato: 6 },
                { freq: 1046.5, start: 0.2, dur: 1.0, type: 'sine' },
            ], 0.18);
        },
    },
    {
        id: 'whoosh',
        label: 'Whoosh',
        emoji: '💨',
        play: () => {
            playNoise(0, 0.4, 400, 0.15);
            playNoise(0.1, 0.3, 1200, 0.1);
        },
    },
    {
        id: 'tada',
        label: 'Tada!',
        emoji: '🌟',
        play: () => {
            // Classic "ta-da!" stinger
            playTones([
                { freq: 392, start: 0, dur: 0.15, type: 'square' },
                { freq: 392, start: 0.15, dur: 0.15, type: 'square' },
                { freq: 392, start: 0.3, dur: 0.15, type: 'square' },
                { freq: 523.25, start: 0.45, dur: 0.6, type: 'square' },
                { freq: 659.25, start: 0.45, dur: 0.6, type: 'square' },
                { freq: 783.99, start: 0.45, dur: 0.6, type: 'square' },
            ], 0.16);
        },
    },
    {
        id: 'bell',
        label: 'Bell',
        emoji: '🛎️',
        play: () => {
            playTones([
                { freq: 587.33, start: 0, dur: 1.2, type: 'sine' },
                { freq: 880, start: 0, dur: 1.2, type: 'sine' },
                { freq: 1174.66, start: 0, dur: 1.2, type: 'sine' },
            ], 0.15);
        },
    },
    {
        id: 'tick',
        label: 'Tick',
        emoji: '⏱️',
        play: () => {
            // Short click — like a clock tick
            const ctx = getCtx();
            const now = ctx.currentTime;
            const osc = ctx.createOscillator();
            osc.type = 'square';
            osc.frequency.value = 1200;
            const g = ctx.createGain();
            g.gain.setValueAtTime(0, now);
            g.gain.linearRampToValueAtTime(0.06, now + 0.001);
            g.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
            osc.connect(g);
            g.connect(ctx.destination);
            osc.start(now);
            osc.stop(now + 0.05);
        },
    },
    {
        id: 'lowbeep',
        label: 'Low Beep',
        emoji: '🔉',
        play: () => {
            // Low, soft, non-intrusive beep — for Exam mode
            playTones([{ freq: 440, start: 0, dur: 0.15, type: 'sine' }], 0.08);
        },
    },
];

export const getKidSound = (id: string): KidSound | undefined =>
    KID_SOUNDS.find((s) => s.id === id);

/** Play a sound by its procedural id (no URL needed). Returns true if found. */
export const playKidSound = (id: string): boolean => {
    const sound = getKidSound(id);
    if (!sound) return false;
    try {
        sound.play();
        return true;
    } catch (err) {
        console.warn('Sound play failed:', err);
        return false;
    }
};
